import { ref } from 'vue'
import { applyAuthSessionPayload, authSession, buildCasLoginUrl, peekReturnTo, saveReturnTo, type AnonymousSessionClaimTarget, type AuthSessionPayload } from '@/auth/session'
import { useConversationStore } from '@/stores/conversationStore'
import {
  AUTH_POPUP_CLOSE_EVENT,
  AUTH_POPUP_MODE_QUERY_KEY,
  AUTH_POPUP_MODE_QUERY_VALUE,
  AUTH_POPUP_PARENT_READY_EVENT,
  AUTH_POPUP_READY_EVENT,
  AUTH_POPUP_SUCCESS_EVENT,
  AUTH_POPUP_WINDOW_NAME,
} from '@/auth/popupProtocol'

const loginRequired = ref(false)
const loginInProgress = ref(false)
const popupBlocked = ref(false)
const loginError = ref<string | null>(null)

let loginPopup: Window | null = null
let popupClosedTimer: number | null = null
let popupMessageListenerRegistered = false
let pendingAnonymousSessionClaim: AnonymousSessionClaimTarget | null = null

interface LoginPopupOptions {
  claimAnonymousSession?: boolean
}

function clearPopupTimer() {
  if (popupClosedTimer != null) {
    window.clearInterval(popupClosedTimer)
    popupClosedTimer = null
  }
}

function monitorPopupClosed() {
  clearPopupTimer()
  popupClosedTimer = window.setInterval(() => {
    if (loginPopup && !loginPopup.closed) return
    clearPopupTimer()
    loginPopup = null
    pendingAnonymousSessionClaim = null
    if (loginInProgress.value) {
      loginInProgress.value = false
      loginError.value = 'Login window closed before sign-in completed.'
    }
  }, 250)
}

function openCasLoginPopup(returnTo?: string): Window | null {
  const popupWidth = 520
  const popupHeight = 760
  const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - popupWidth) / 2))
  const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - popupHeight) / 2))
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
  const existingReturnTo = peekReturnTo()
  saveReturnTo(returnTo ?? existingReturnTo ?? currentPath)
  const loginUrl = new URL(buildCasLoginUrl())
  const serviceUrl = loginUrl.searchParams.get('service')
  if (serviceUrl) {
    const callbackUrl = new URL(serviceUrl)
    callbackUrl.searchParams.set(AUTH_POPUP_MODE_QUERY_KEY, AUTH_POPUP_MODE_QUERY_VALUE)
    loginUrl.searchParams.set('service', callbackUrl.toString())
  } else {
    loginUrl.searchParams.set(AUTH_POPUP_MODE_QUERY_KEY, AUTH_POPUP_MODE_QUERY_VALUE)
  }

  return window.open(
    loginUrl.toString(),
    AUTH_POPUP_WINDOW_NAME,
    `popup=yes,width=${popupWidth},height=${popupHeight},left=${left},top=${top},resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,status=no`,
  )
}

function startLoginPopup(returnTo?: string, options: LoginPopupOptions = {}) {
  loginError.value = null
  popupBlocked.value = false
  pendingAnonymousSessionClaim = null

  const popup = openCasLoginPopup(returnTo)
  if (!popup) {
    loginInProgress.value = false
    popupBlocked.value = true
    loginError.value = 'Popup was blocked by the browser. Allow popups and try again.'
    return
  }

  loginPopup = popup
  if (options.claimAnonymousSession && !authSession.isAuthenticated && authSession.authSource == null) {
    pendingAnonymousSessionClaim = useConversationStore().getActiveAnonymousSessionClaimTarget()
  }
  loginInProgress.value = true
  monitorPopupClosed()
}

function retryLoginPopup(returnTo?: string, options: LoginPopupOptions = {}) {
  startLoginPopup(returnTo, options)
}

function markLoginRequired() {
  loginRequired.value = true
  loginInProgress.value = false
  popupBlocked.value = false
  loginError.value = null
}

async function handleAuthPopupMessage(event: MessageEvent) {
  if (event.origin !== window.location.origin) return
  if (!event.data || typeof event.data.type !== 'string') return

  if (event.data.type === AUTH_POPUP_READY_EVENT) {
    const source = event.source as Window | null
    if (source && typeof source.postMessage === 'function') {
      source.postMessage({ type: AUTH_POPUP_PARENT_READY_EVENT }, window.location.origin)
    }
    return
  }

  if (event.data.type !== AUTH_POPUP_SUCCESS_EVENT) return
  if (!loginPopup || event.source !== loginPopup) return
  const payload = event.data.payload as AuthSessionPayload | undefined
  if (!payload || !applyAuthSessionPayload(payload)) {
    loginInProgress.value = false
    loginError.value = 'Received an invalid login response. Try again.'
    return
  }

  loginRequired.value = false
  popupBlocked.value = false
  loginError.value = null
  clearPopupTimer()

  const source = event.source as Window | null
  if (source && typeof source.postMessage === 'function') {
    source.postMessage({ type: AUTH_POPUP_CLOSE_EVENT }, window.location.origin)
  }
  loginPopup = null

  try {
    const claimTarget = pendingAnonymousSessionClaim
    pendingAnonymousSessionClaim = null
    if (claimTarget && authSession.authSource === 'cas') {
      await useConversationStore().claimAnonymousSession(claimTarget)
    }
  } catch (error) {
    console.warn('[auth] Failed to claim the anonymous conversation after sign-in.', error)
  } finally {
    loginInProgress.value = false
  }
}

function ensurePopupMessageListener() {
  if (popupMessageListenerRegistered || typeof window === 'undefined') return
  window.addEventListener('message', handleAuthPopupMessage)
  popupMessageListenerRegistered = true
}

/**
 * Returns the tab-wide CAS popup login coordinator.
 *
 * The popup and its window-message listener are shared so every login entry
 * point observes the same in-progress state and authentication result.
 */
export function useAuthPopupRecovery() {
  ensurePopupMessageListener()

  return {
    loginRequired,
    loginInProgress,
    popupBlocked,
    loginError,
    markLoginRequired,
    startLoginPopup,
    retryLoginPopup,
  }
}
